import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { angemeldeterBenutzer } from './angemeldeterBenutzer';
import { auszugHash, istAusgabeUnterlage, type Abweichung } from './projektSteckbrief/abweichungen';

/**
 * Ausgaben und Fassungsentscheide der Folgeunterlagen (Plan Stufe 6.1/6.3,
 * Migration Nr. 163).
 *
 * ═══ ZWEI TABELLEN, BEIDE NUR ANHAENGEND ═════════════════════════════════
 *   `projekt_unterlagen_ausgaben`   — je Freigabe oder Versand EINE Zeile mit
 *                                     dem eingefrorenen Steckbrief-Auszug. Das
 *                                     ist der Anker des Aenderungsdiensts: nicht
 *                                     „wann zuletzt gespeichert", sondern „was
 *                                     stand drin, als die Fassung hinausging".
 *   `unterlagen_fassungsentscheide` — je Bewertung einer Abweichung EINE Zeile:
 *                                     neue Fassung, nicht erheblich (mit
 *                                     Begruendung), spaeter. Der Nachweis, dass
 *                                     der Koordinator die Aenderung bewertet hat
 *                                     (BaustellV § 2 Abs. 2, § 3 Abs. 3 Nr. 3).
 * Nichts wird geaendert oder geloescht. Eine Korrektur ist eine weitere Zeile.
 *
 * ═══ LESEN FAIL-OPEN, ABER NIE STILL ══════════════════════════════════════
 * Tabelle fehlt (Nr. 163 noch nicht gefahren), Netz weg, Regel greift: das
 * Lesen liefert leer PLUS `lesefehler`. Wer `lesefehler` ignoriert, liest
 * „keine Ausgabe → nichts veraltet" — deshalb steht der Fehler als Feld
 * daneben, und der Hook reicht ihn an die Oberflaeche.
 *
 * ═══ SCHREIBEN WIRFT NIE ══════════════════════════════════════════════════
 * `legeAusgabeAn` haengt an Freigabe und Versand; ein Fehler hier darf die
 * Freigabe nicht ungueltig machen. Ergebnis ist ein Status mit Klartext.
 * Dieselbe Fassung zum selben Anlass zweimal (Teil-Unique auf `project_id,
 * unterlage, fassung_ref, anlass`) ergibt `schon_vorhanden` mit der
 * vorhandenen Zeile, nie einen Fehler — „erneut ablegen" nach einer verlorenen
 * Antwort verdoppelt nichts. Der ANLASS gehoert zur Kennung: dieselbe Fassung
 * wird erst freigegeben und dann versendet, und beides steht im Journal.
 *
 * ═══ WERTEMENGE DER UNTERLAGEN ════════════════════════════════════════════
 * `AusgabeUnterlage` folgt dem CHECK der Tabelle und damit `ErzeugendesModul`
 * der Ablage (`unterlage`, `baustellenordnung`) — NICHT dem Steckbrief, der
 * `ufsa` und `bauordnung` sagt. Die Bruecke ist `FOLGEUNTERLAGE_JE_AUSGABE`
 * in `projektSteckbrief/abweichungen.ts`.
 */

export type AusgabeUnterlage = 'vorankuendigung' | 'sigeplan' | 'asplan' | 'unterlage' | 'baustellenordnung' | 'handbuch';

export type AusgabeAnlass = 'freigabe' | 'versand';

export interface UnterlagenAusgabe {
    id: string;
    project_id: string;
    unterlage: AusgabeUnterlage;
    /** Fassungskennung der abgelegten Zeile (`project_documents.fassung_ref`); `null` ohne Kennung. */
    fassung_ref: string | null;
    anlass: AusgabeAnlass;
    /** Der eingefrorene Steckbrief-Auszug (`steckbriefAuszugFuerAusgabe`). */
    auszug: Record<string, unknown>;
    auszug_hash: string;
    /** Die abgelegte Fassung in der Projektablage (`project_documents.id`). */
    dokument_id: string | null;
    erstellt_am: string;
    erstellt_von_name: string | null;
    /**
     * Wer die Zeile angelegt hat (`auth.uid()`). Optional und nur gesetzt,
     * wenn die Zeile es traegt: der Abschlussschritt (Plan Paketposition B5)
     * wertet eine schon liegende Ausgabe nur dann als die EIGENE eines
     * wiederholten Vorgangs, wenn sie von derselben Person stammt.
     */
    erstellt_von?: string;
}

export type FassungsEntscheid = 'neue_fassung' | 'nicht_erheblich' | 'spaeter';

export interface Fassungsentscheidung {
    id: string;
    project_id: string;
    unterlage: AusgabeUnterlage;
    ausgabe_id: string;
    /** `abweichungsHash` der bewerteten Aenderung — Kennung, fuer die nicht erneut gefragt wird. */
    abweichung_hash: string;
    entscheid: FassungsEntscheid;
    begruendung: string | null;
    entschieden_am: string;
    entschieden_von_name: string | null;
}

const T_AUSGABEN = 'projekt_unterlagen_ausgaben';
const T_ENTSCHEIDE = 'unterlagen_fassungsentscheide';

/** PostgREST deckelt Antworten ohne Meldung bei 1000 Zeilen. */
const SEITE = 1000;

const ENTSCHEIDE: readonly FassungsEntscheid[] = ['neue_fassung', 'nicht_erheblich', 'spaeter'];

/** Mindestlaenge der Begruendung bei „nicht erheblich" — wie der CHECK der Tabelle. */
export const BEGRUENDUNG_MINDESTLAENGE = 5;

// ═══════════════════════════════════════════════════════════════════════════
// HILFEN
// ═══════════════════════════════════════════════════════════════════════════

const istObjekt = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

const text = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));
const textOderNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

export const istFassungsEntscheid = (v: unknown): v is FassungsEntscheid =>
    typeof v === 'string' && (ENTSCHEIDE as readonly string[]).includes(v);

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';

const klartext = (e: unknown): string => {
    if (istFehlendeTabelle(e)) {
        return 'Die Tabellen des Änderungsdiensts fehlen noch (Migration Nr. 163). Bitte an die Verwaltung wenden.';
    }
    return describeError(e);
};

/**
 * Eine Zeile aus `projekt_unterlagen_ausgaben`. `null` nur bei unbekannter
 * Unterlage — ein unbekannter Anlass wird als `freigabe` gelesen, denn der
 * Anker zaehlt; eine verschwiegene Ausgabe hiesse „nichts veraltet".
 */
const mapAusgabe = (row: Record<string, unknown>): UnterlagenAusgabe | null => {
    if (!istAusgabeUnterlage(row.unterlage)) return null;
    return {
        id: text(row.id),
        project_id: text(row.project_id),
        unterlage: row.unterlage,
        fassung_ref: textOderNull(row.fassung_ref),
        anlass: row.anlass === 'versand' ? 'versand' : 'freigabe',
        auszug: istObjekt(row.auszug) ? row.auszug : {},
        auszug_hash: text(row.auszug_hash),
        dokument_id: textOderNull(row.dokument_id),
        erstellt_am: text(row.erstellt_am),
        erstellt_von_name: textOderNull(row.erstellt_von_name),
        // Rein additiv: nur, wenn die Zeile es traegt.
        ...(textOderNull(row.erstellt_von) ? { erstellt_von: textOderNull(row.erstellt_von) as string } : {}),
    };
};

const mapEntscheidung = (row: Record<string, unknown>): Fassungsentscheidung | null => {
    if (!istAusgabeUnterlage(row.unterlage) || !istFassungsEntscheid(row.entscheid)) return null;
    return {
        id: text(row.id),
        project_id: text(row.project_id),
        unterlage: row.unterlage,
        ausgabe_id: text(row.ausgabe_id),
        abweichung_hash: text(row.abweichung_hash),
        entscheid: row.entscheid,
        begruendung: textOderNull(row.begruendung),
        entschieden_am: text(row.entschieden_am),
        entschieden_von_name: textOderNull(row.entschieden_von_name),
    };
};

const alsZeilen = (data: unknown): Record<string, unknown>[] =>
    Array.isArray(data) ? data.filter(istObjekt) : [];

// ═══════════════════════════════════════════════════════════════════════════
// AUSGABEN
// ═══════════════════════════════════════════════════════════════════════════

export interface LetzteAusgaben {
    /** Je Unterlage die juengste Ausgabe; fehlt, wenn nie freigegeben oder versendet. */
    ausgaben: Partial<Record<AusgabeUnterlage, UnterlagenAusgabe>>;
    /** Gesetzt, wenn gelesen werden sollte, aber nicht konnte — dann sagt `ausgaben` NICHTS. */
    lesefehler: string | null;
}

const KEINE_AUSGABEN: LetzteAusgaben = { ausgaben: {}, lesefehler: null };

/**
 * Die letzte Ausgabe je Unterlage. Eine Abfrage, absteigend nach Zeit: die
 * ersten Zeilen je Unterlage sind die juengsten; mehr als `SEITE` Ausgaben
 * hat kein Projekt, bevor sechs Unterlagen je einmal vorkamen.
 */
export const fetchLetzteAusgaben = async (projectId: string): Promise<LetzteAusgaben> => {
    if (!projectId || projectId === 'temp-id') return KEINE_AUSGABEN;
    try {
        const { data, error } = await supabase
            .from(T_AUSGABEN)
            .select('*')
            .eq('project_id', projectId)
            .order('erstellt_am', { ascending: false })
            // Zweites Kriterium: zwei Ausgaben in derselben Sekunde sollen
            // bei jedem Lesen dieselbe Reihenfolge haben.
            .order('id', { ascending: false })
            .limit(SEITE);
        if (error) return { ausgaben: {}, lesefehler: klartext(error) };
        const ausgaben: Partial<Record<AusgabeUnterlage, UnterlagenAusgabe>> = {};
        alsZeilen(data).forEach(row => {
            const a = mapAusgabe(row);
            if (a && !ausgaben[a.unterlage]) ausgaben[a.unterlage] = a;
        });
        return { ausgaben, lesefehler: null };
    } catch (e) {
        return { ausgaben: {}, lesefehler: klartext(e) };
    }
};

/**
 * Die Ausgabe einer Fassung zu EINEM Anlass — ueber die Teil-Unique-Kennung
 * `(project_id, unterlage, fassung_ref, anlass)`. Ohne den Anlass fiele der
 * Versand einer schon freigegebenen Fassung als `schon_vorhanden` heraus.
 * Wirft bei Lesefehler.
 */
export const findeAusgabe = async (
    projectId: string,
    unterlage: AusgabeUnterlage,
    fassungRef: string,
    anlass: AusgabeAnlass,
): Promise<UnterlagenAusgabe | null> => {
    const { data, error } = await supabase
        .from(T_AUSGABEN)
        .select('*')
        .eq('project_id', projectId)
        .eq('unterlage', unterlage)
        .eq('fassung_ref', fassungRef)
        .eq('anlass', anlass)
        .order('erstellt_am', { ascending: true })
        .limit(1);
    if (error) throw new Error(klartext(error));
    const zeile = alsZeilen(data)[0];
    return zeile ? mapAusgabe(zeile) : null;
};

/**
 * Irgendeine Ausgabe zu dieser Fassung — gleich zu welchem Anlass, die
 * juengste zuerst. Fuer die Ablage: ob eine Fassung ueberwacht wird,
 * entscheidet, dass IRGENDEIN Anker zu ihr liegt — eine freigegebene und
 * danach versendete Fassung hat ihren von der Freigabe, und „kein Anker zum
 * Versand" waere eine Falschmeldung (`unterlagenAblage.ts`). Wirft bei
 * Lesefehler.
 */
export const findeAusgabeZurFassung = async (
    projectId: string,
    unterlage: AusgabeUnterlage,
    fassungRef: string,
): Promise<UnterlagenAusgabe | null> => {
    const { data, error } = await supabase
        .from(T_AUSGABEN)
        .select('*')
        .eq('project_id', projectId)
        .eq('unterlage', unterlage)
        .eq('fassung_ref', fassungRef)
        .order('erstellt_am', { ascending: false })
        .limit(1);
    if (error) throw new Error(klartext(error));
    const zeile = alsZeilen(data)[0];
    return zeile ? mapAusgabe(zeile) : null;
};

export interface AusgabeAnlegen {
    projectId: string;
    unterlage: AusgabeUnterlage;
    /** Fassungskennung der abgelegten Zeile — verhindert zusammen mit `anlass` die Doppelausgabe. */
    fassungRef: string | null;
    anlass: AusgabeAnlass;
    /** `steckbriefAuszugFuerAusgabe(steckbrief, unterlage)` zum Zeitpunkt der Freigabe. */
    auszug: Record<string, unknown>;
    dokumentId: string | null;
}

export interface AusgabeErgebnis {
    status: 'ok' | 'schon_vorhanden' | 'fehler';
    ausgabe: UnterlagenAusgabe | null;
    fehler: string | null;
}

const ausgabeFehler = (fehler: string): AusgabeErgebnis => ({ status: 'fehler', ausgabe: null, fehler });

/** Die Absage VOR jedem Netzzugriff — oder `null`. */
export const pruefeAusgabeAuftrag = (a: AusgabeAnlegen): string | null => {
    if (!a.projectId || a.projectId === 'temp-id') {
        return 'Das Projekt muss erst gespeichert werden, bevor eine Ausgabe festgehalten werden kann.';
    }
    if (!istAusgabeUnterlage(a.unterlage)) return `Unbekannte Unterlage „${String(a.unterlage)}" — die Ausgabe wurde nicht festgehalten.`;
    if (a.anlass !== 'freigabe' && a.anlass !== 'versand') return 'Eine Ausgabe entsteht nur bei Freigabe oder Versand, nicht fuer einen Entwurf.';
    if (!istObjekt(a.auszug)) return 'Ohne Steckbrief-Auszug gibt es nichts festzuhalten.';
    return null;
};

/**
 * Haelt die Ausgabe einer Fassung fest. Wirft NIE.
 *
 * Reihenfolge: Pruefung ohne Netz → Vorab-Blick auf `fassungRef` + `anlass`
 * → Zeile anhaengen → bei 23505 die Zeile des Gewinners holen (Wettlauf
 * zweier Ablagen derselben Fassung zum selben Anlass).
 */
export const legeAusgabeAn = async (a: AusgabeAnlegen): Promise<AusgabeErgebnis> => {
    const ref = (a.fassungRef || '').trim() || null;
    try {
        const absage = pruefeAusgabeAuftrag(a);
        if (absage) return ausgabeFehler(absage);

        if (ref) {
            const vorhanden = await findeAusgabe(a.projectId, a.unterlage, ref, a.anlass);
            if (vorhanden) return { status: 'schon_vorhanden', ausgabe: vorhanden, fehler: null };
        }

        const wer = await angemeldeterBenutzer();
        const zeile = {
            project_id: a.projectId,
            unterlage: a.unterlage,
            fassung_ref: ref,
            anlass: a.anlass,
            auszug: a.auszug,
            auszug_hash: auszugHash(a.auszug),
            dokument_id: (a.dokumentId || '').trim() || null,
            erstellt_von_name: wer.name,
            // Ohne Id bleibt die Spalte der Datenbank ueberlassen (Vorgabe `auth.uid()`).
            ...(wer.id ? { erstellt_von: wer.id } : {}),
        };
        const { data, error } = await supabase
            .from(T_AUSGABEN)
            .insert(zeile)
            .select('*')
            .single();
        if (error) {
            if (istDublette(error) && ref) {
                const gewinner = await findeAusgabe(a.projectId, a.unterlage, ref, a.anlass);
                if (gewinner) return { status: 'schon_vorhanden', ausgabe: gewinner, fehler: null };
            }
            return ausgabeFehler(klartext(error));
        }
        // Die Zeile IST geschrieben — eine unlesbare Antwort macht daraus keinen Fehler.
        const ausgabe = (istObjekt(data) ? mapAusgabe(data) : null) || {
            id: istObjekt(data) ? text(data.id) : '',
            project_id: a.projectId,
            unterlage: a.unterlage,
            fassung_ref: ref,
            anlass: a.anlass,
            auszug: a.auszug,
            auszug_hash: zeile.auszug_hash,
            dokument_id: zeile.dokument_id,
            erstellt_am: istObjekt(data) ? text(data.erstellt_am) : '',
            erstellt_von_name: wer.name,
        };
        return { status: 'ok', ausgabe, fehler: null };
    } catch (e) {
        return ausgabeFehler(klartext(e));
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// FASSUNGSENTSCHEIDE
// ═══════════════════════════════════════════════════════════════════════════

export interface Entscheidungen {
    /** Absteigend nach `entschieden_am`. */
    entscheidungen: Fassungsentscheidung[];
    lesefehler: string | null;
}

export const fetchEntscheidungen = async (projectId: string): Promise<Entscheidungen> => {
    if (!projectId || projectId === 'temp-id') return { entscheidungen: [], lesefehler: null };
    try {
        const { data, error } = await supabase
            .from(T_ENTSCHEIDE)
            .select('*')
            .eq('project_id', projectId)
            .order('entschieden_am', { ascending: false })
            .order('id', { ascending: false })
            .limit(SEITE);
        if (error) return { entscheidungen: [], lesefehler: klartext(error) };
        const entscheidungen = alsZeilen(data)
            .map(mapEntscheidung)
            .filter((e): e is Fassungsentscheidung => e !== null);
        return { entscheidungen, lesefehler: null };
    } catch (e) {
        return { entscheidungen: [], lesefehler: klartext(e) };
    }
};

export interface EntscheidungSpeichern {
    projectId: string;
    unterlage: AusgabeUnterlage;
    ausgabeId: string;
    abweichungHash: string;
    /** Die bewerteten Abweichungen — so, wie der Dialog sie gezeigt hat. */
    abweichungen: Abweichung[];
    entscheid: FassungsEntscheid;
    begruendung: string | null;
}

export interface EntscheidungErgebnis {
    status: 'ok' | 'fehler';
    fehler: string | null;
}

/**
 * Die Absage VOR jedem Netzzugriff — oder `null`. Dieselben Regeln wie die
 * CHECKs der Tabelle, damit der Dialog sie VOR dem Klick zeigen kann.
 */
export const pruefeEntscheidung = (e: EntscheidungSpeichern): string | null => {
    if (!e.projectId || e.projectId === 'temp-id') return 'Das Projekt muss erst gespeichert werden.';
    if (!istAusgabeUnterlage(e.unterlage)) return `Unbekannte Unterlage „${String(e.unterlage)}".`;
    if (!(e.ausgabeId || '').trim()) return 'Ohne Ausgabe gibt es keine Fassung, zu der entschieden werden koennte.';
    if (!(e.abweichungHash || '').trim()) return 'Die Kennung der Aenderung fehlt.';
    if (!istFassungsEntscheid(e.entscheid)) return `Unbekannter Entscheid „${String(e.entscheid)}".`;
    if (e.entscheid === 'nicht_erheblich' && (e.begruendung || '').trim().length < BEGRUENDUNG_MINDESTLAENGE) {
        return `„Nicht erheblich" braucht eine Begründung (mindestens ${BEGRUENDUNG_MINDESTLAENGE} Zeichen) — sie ist der Nachweis der Bewertung.`;
    }
    return null;
};

/** Eine Abweichung, wie sie in `abweichungen jsonb[]` steht — nur Daten, keine Funktionen. */
const abweichungFuerDb = (a: Abweichung): Record<string, unknown> => ({
    pfad: a.pfad,
    label: a.label,
    alt: a.alt === undefined ? null : a.alt,
    neu: a.neu === undefined ? null : a.neu,
    altText: a.altText,
    neuText: a.neuText,
});

/** Haengt eine Entscheidung an. Wirft NIE. */
export const speichereEntscheidung = async (e: EntscheidungSpeichern): Promise<EntscheidungErgebnis> => {
    try {
        const absage = pruefeEntscheidung(e);
        if (absage) return { status: 'fehler', fehler: absage };

        const wer = await angemeldeterBenutzer();
        const { error } = await supabase
            .from(T_ENTSCHEIDE)
            .insert({
                project_id: e.projectId,
                unterlage: e.unterlage,
                ausgabe_id: e.ausgabeId,
                abweichung_hash: e.abweichungHash,
                abweichungen: (e.abweichungen || []).map(abweichungFuerDb),
                entscheid: e.entscheid,
                begruendung: (e.begruendung || '').trim() || null,
                entschieden_von_name: wer.name,
                ...(wer.id ? { entschieden_von: wer.id } : {}),
            });
        if (error) return { status: 'fehler', fehler: klartext(error) };
        return { status: 'ok', fehler: null };
    } catch (err) {
        return { status: 'fehler', fehler: klartext(err) };
    }
};
